import React, { useEffect, useState } from 'react';
import Breadcrumbs from '@/components/Breadcrumbs';
import { TabPanel } from '@/components/Tabs';
import { batchPaymentsPage } from '@/utils/translations';
import { useManagePayment } from '../hooks/useManagePayment';
import {
  PAYMENT_GROUPS,
  PaymentGroup,
  PaymentGroupEnum,
  TPaymentListItem
} from '../types/ManagePayments';
import { getPaymentPresentationStrategy } from '../data/paymentPresentationStrategies';
import PaymentsTabBar from './PaymentsTabBar';
import PaymentsTable from './PaymentsTable';
import { MoveMoneyMenu } from '@/features/home/components/NavigationBar/MoveMoneyMenu';
import { useBreadcrumbs } from '@/features/home/hooks/useBreadcrumbs';

const EMPTY_MESSAGE: Record<PaymentGroup, string> = {
  [PaymentGroupEnum.SCHEDULED]: batchPaymentsPage.emptyState.scheduled,
  [PaymentGroupEnum.ACTION_REQUIRED]: batchPaymentsPage.emptyState.action_required,
  [PaymentGroupEnum.BATCH_HISTORY]: batchPaymentsPage.emptyState.batch_history
};

interface PaymentsProps {
  showBreadcrumbs?: boolean;
  showMoveMoney?: boolean;
}

const Payments: React.FC<PaymentsProps> = ({ showBreadcrumbs = true, showMoveMoney = false }) => {
  const {
    rowsByGroup,
    counts,
    activeTab,
    setActiveTab,
    isLoading,
    applyPaymentUpdate,
    removePayment,
    hasBatchPaymentAccess
  } = useManagePayment();
  const [showMoveMoneyMenu, setShowMoveMoneyMenu] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<TPaymentListItem | null>(null);
  const { homeBreadcrumb } = useBreadcrumbs();

  // Which modal to open is entirely owned by the row's kind strategy (see
  // data/paymentPresentationStrategies.ts) - adding a new kind never means adding another branch
  // here, just registering its own Modal there.
  const SelectedModal = selectedPayment
    ? getPaymentPresentationStrategy(selectedPayment.kind).Modal
    : null;

  // Keeps an already-open modal's payment prop current with the list - e.g. an IN_PROGRESS batch
  // being polled in the background, or a Retry/Mark as complete result - instead of freezing at
  // whatever it was when the row was first clicked. Left untouched if the row has disappeared
  // from every tab (a delete/removal already closes its modal via onPaymentDelete).
  useEffect(() => {
    setSelectedPayment((current) => {
      if (!current) return current;
      const fresh = PAYMENT_GROUPS.flatMap((group) => rowsByGroup[group]).find(
        (item) => item.id === current.id
      );
      return fresh ?? current;
    });
  }, [rowsByGroup]);

  return (
    <>
      <div className="p-4 py-6 flex flex-col gap-y-4 bg-aux-lightgray relative">
        {showBreadcrumbs && <Breadcrumbs paths={homeBreadcrumb} />}
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-y-4">
            <h3 className="text-2xl font-medium text-emerge-navy">{batchPaymentsPage.title}</h3>
            <h4 className="text-sm font-normal leading-5 text-dark-navy-30">
              {batchPaymentsPage.subtitle}
            </h4>
          </div>
          {showMoveMoney && (
            <MoveMoneyMenu
              showNavBarMenu={showMoveMoneyMenu ? 'movemoney' : undefined}
              onOpenNavigation={() => setShowMoveMoneyMenu((prev) => !prev)}
              onCloseMenu={() => setShowMoveMoneyMenu(false)}
            />
          )}
        </div>
      </div>
      <div className="bg-aux-lightgray w-full">
        <PaymentsTabBar
          activeTab={activeTab}
          onChange={setActiveTab}
          counts={counts}
          showBatchHistory={hasBatchPaymentAccess}
        />
      </div>
      <div className="flex flex-col gap-y-4">
        {PAYMENT_GROUPS.map((group) => (
          <TabPanel key={group} id={group} activeTab={activeTab}>
            <PaymentsTable
              rows={rowsByGroup[group]}
              emptyMessage={EMPTY_MESSAGE[group]}
              isLoading={isLoading}
              onRowClick={setSelectedPayment}
            />
          </TabPanel>
        ))}
      </div>
      {SelectedModal && (
        <SelectedModal
          isOpen={!!selectedPayment}
          onClose={() => setSelectedPayment(null)}
          payment={selectedPayment}
          onPaymentUpdate={applyPaymentUpdate}
          onPaymentDelete={removePayment}
        />
      )}
    </>
  );
};

export default Payments;
